import type { ProviderBadgeDto } from './ai-provider-mode.js';
import type { BaseEntityDto, Id } from './common.js';

export const USER_ROLES = [
  'owner',
  'admin',
  'agent',
  'manager',
  'sales',
  'finance',
  'inventory',
  'editor',
  'technician',
  'delivery',
  'accountant',
  'sales_inventory',
] as const;
export type UserRole = (typeof USER_ROLES)[number];

/**
 * Acesso separado aos dados de saúde. O cargo comum continua controlando as
 * ferramentas administrativas; este nível controla somente informações
 * sensíveis de pacientes e nunca é concedido por consequência do cargo.
 */
export const CLINICAL_ACCESS_LEVELS = ['none', 'read', 'write'] as const;
export type ClinicalAccessLevel = (typeof CLINICAL_ACCESS_LEVELS)[number];

/**
 * Acesso administrativo a carteirinhas, elegibilidade, autorizações e guias.
 * É separado do prontuário: o financeiro pode cobrar sem ler evolução clínica,
 * e um profissional assistencial não recebe dados financeiros por consequência.
 */
export const HEALTH_BILLING_ACCESS_LEVELS = ['none', 'read', 'write'] as const;
export type HealthBillingAccessLevel = (typeof HEALTH_BILLING_ACCESS_LEVELS)[number];

/**
 * O ramo define vocabulário, segurança e preparação inicial. `clinic` existe
 * separado de `dental`: uma clínica multiprofissional não pode herdar catálogo,
 * políticas ou textos odontológicos por exclusão.
 */
export const COMPANY_VERTICALS = ['dental', 'clinic', 'generic'] as const;
export type CompanyVertical = (typeof COMPANY_VERTICALS)[number];

export interface BusinessUnitDto {
  id: string;
  code: string;
  name: string;
  kind: 'headquarters' | 'branch' | 'office';
  address: string;
  businessHours: string;
  timezone: string;
  currency: string;
  stockLocations: string[];
  isActive: boolean;
}

export interface CompanyDto extends BaseEntityDto {
  /** Null/ausente em contas antigas: nunca inventar um novo prazo no login. */
  trialPeriod?: { startedAt: string; endsAt: string; status: 'active' | 'ended' } | null;
  address?: string;
  businessHours?: string;
  name: string;
  slug: string;
  timezone: string;
  currency: string;
  locale: string;
  /** Perfil inicial. O perfil neutro não instala templates ou assistentes. */
  vertical: CompanyVertical;
  isDemo: boolean;
  /** Only returned for an explicitly configured isolated local rehearsal. */
  localBusinessTime?: string;
  units: BusinessUnitDto[];
  unitsRevision: number;
}

export const ONLINE_OPERATIONS_ACCESS_LEVELS = ['none', 'read', 'write'] as const;
export type OnlineOperationsAccessLevel = (typeof ONLINE_OPERATIONS_ACCESS_LEVELS)[number];

export interface UserDto extends BaseEntityDto {
  companyId: Id;
  name: string;
  email: string;
  role: UserRole;
  /** Nivel EFETIVO: `none` quando o papel nao pode ter acesso clinico, mesmo com valor gravado. */
  clinicalAccess: ClinicalAccessLevel;
  /** So para owner/admin e so quando o valor gravado difere do efetivo (usuario legado). */
  clinicalAccessStored?: ClinicalAccessLevel;
  healthBillingAccess: HealthBillingAccessLevel;
  /** Individual grant; legacy accounts default to none. Does not grant finances. */
  onlineOperationsAccess?: OnlineOperationsAccessLevel;
  isActive: boolean;
  absence: UserAbsenceDto | null;
}

export interface UserAbsenceDto {
  startsOn: string;
  endsOn: string;
  backupUserId: Id | null;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthSessionDto {
  accessToken: string;
  expiresIn: number;
  user: UserDto;
  company: CompanyDto;
  features: FeatureFlagsDto;
  /** R058 - vai junto da sessão para a área do operador existir já no primeiro desenho. */
  operadorDaPlataforma: boolean;
  /**
   * R032 - quem responde pelo atendimento automatico nesta instalacao.
   *
   * Vai na SESSAO, e nao numa tela de diagnostico, porque o criterio pede que o
   * modo de simulacao seja SEMPRE identificado. Numa tela que alguem precisa
   * abrir, ele nao e - e o caso perigoso e justamente o de quem nao desconfia
   * de nada: o produto responde, responde bem o bastante, e a descoberta vem
   * semanas depois.
   */
  aiProvider: ProviderBadgeDto;
}

/**
 * CONVERSA CENTRAL - o que esta ligado nesta instalacao.
 *
 * A flag vive no SERVIDOR e chega ao cliente pela sessao, e nao por variavel de
 * build: assim ligar ou desligar a experiencia nao exige recompilar o front, e
 * as duas pontas nunca discordam sobre o que esta disponivel.
 *
 * Ela nao substitui autorizacao. Quem nao pode configurar continua nao podendo,
 * com a flag ligada ou desligada — o servidor valida papel em toda rota
 * administrativa, independentemente disto.
 */
export interface FeatureFlagsDto {
  /** A conversa central de gestao ("Conversar com Rimati"). */
  rimatiChat: boolean;
  /** R025 - a area do operador da plataforma, separada de Minha empresa. */
  platformAdmin: boolean;
  /** R034 - a jornada guiada de conectar o WhatsApp da empresa. */
  whatsappGuidedSetup: boolean;
  /** R041-R048 - transcricao de audio recebido. */
  audioTranscription: boolean;
  /** R049-R056 - assinatura e cobranca do proprio Rimati. */
  billing: boolean;
  /** R081-R088 - o financeiro da empresa cliente. */
  financeModule: boolean;
  /** R089-R096 - o estoque da empresa cliente. */
  stockModule: boolean;
  /** LANC-a (C6) - emissão de nota fiscal (desligada até haver provedor fiscal). */
  fiscalEmission: boolean;
}

export interface MeDto {
  user: UserDto;
  company: CompanyDto;
  features: FeatureFlagsDto;
  aiProvider: ProviderBadgeDto;
  /**
   * R058 - quem entrou opera a PLATAFORMA, e não só a própria clínica.
   *
   * A tela precisa saber disso para existir: sem o campo, a área do operador
   * teria de ser adivinhada por tentativa — pedir e ver se vem 404 —, e uma
   * interface que descobre permissão por erro mostra e esconde coisa piscando.
   *
   * **Não é autorização.** O servidor confere o operador a cada requisição, com
   * a flag da plataforma e o registro próprio. Este campo só diz o que DESENHAR;
   * mentir nele não abre porta nenhuma.
   */
  operadorDaPlataforma: boolean;
}

export interface UpdateCompanyRequest {
  address?: string;
  businessHours?: string;
  name?: string;
  timezone?: string;
  currency?: string;
  locale?: string;
  units?: BusinessUnitDto[];
  unitsRevision?: number;
}

export interface CreateUserRequest {
  name: string;
  email: string;
  role: UserRole;
  clinicalAccess?: ClinicalAccessLevel;
  healthBillingAccess?: HealthBillingAccessLevel;
  onlineOperationsAccess?: OnlineOperationsAccessLevel;
}

/** A senha temporaria vem UMA vez, na resposta do convite. Nao e recuperavel. */
export interface CreateUserResultDto {
  user: UserDto;
  temporaryPassword: string;
}

export interface UpdateUserRequest {
  name?: string;
  role?: UserRole;
  clinicalAccess?: ClinicalAccessLevel;
  healthBillingAccess?: HealthBillingAccessLevel;
  onlineOperationsAccess?: OnlineOperationsAccessLevel;
  isActive?: boolean;
  absence?: UserAbsenceDto | null;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

/** Empresas autorizadas da identidade; não inclui credenciais nem IDs de contas alheias. */
export interface AuthorizedCompanyDto {
  directLink?: boolean;
  companyId: string;
  name: string;
  slug: string;
  role: UserRole;
  current: boolean;
}
